<script>
    import { createEventDispatcher } from 'svelte'
    import { Alert, Row, Col, Button } from 'sveltestrap'
    import { plus, infoCircle } from 'svelte-awesome/icons'
    import Icon from 'svelte-awesome'
    import { InputText, Select, InputFile } from '@fno/component2/form.js'
    import { postPublicFile } from '@fno/service/api/files'
    import FormAlerts from '@fno/route/tab/task/FormAlerts.svelte'
    import FormSavingButtons from '@fno/route/tab/task/FormSavingButtons.svelte'
    import UserInfo from './UserInfo.svelte'
    import timezones from 'fanx-schema/value/timezone'
    import { currentSchool } from '@fno/service/api/schools.js'
    import { get } from 'dot-prop'
    export let context
    export let saving
    export let schoolId

    const {
        tier,
        orgsData,
        school
    } = $currentSchool

    let rewardsEnabled = false

    $: primaryLogo = get(context, 'form.clientOnboarding.attributes.steps.GENERAL_INFO.primaryAppLogo.key', false) && `https://${context.form.clientOnboarding.attributes.steps.GENERAL_INFO.primaryAppLogo.bucket}/${context.form.clientOnboarding.attributes.steps.GENERAL_INFO.primaryAppLogo.key}`
    $: selectableTimezones = Object.keys(timezones).map((id) => ({
        id,
        name: timezones[id]
    }))

    $: {
        rewardsEnabled = context.form.schoolOnboarding.attributes.steps.KICK_OFF_PREP.usingRewards || context.form.school.attributes.featuresEnabledInPortal.rewardsConfiguration
    }

    const dispatch = createEventDispatcher()
    const validateIconName = (event) => {
        const appStoreNameParts = event.detail.context.form.schoolOnboarding.attributes.steps.INITIAL_DESIGN_REVIEW.appStoreName.toLowerCase().split(" ");
        const appIconNameParts = event.detail.value.toLowerCase().split(" ")

        const valid = appStoreNameParts.filter(value => appIconNameParts.includes(value)).length > 0 ? true : false;

        if (!valid) {
            event.detail.context.alerts = [
                {
                    error: {
                        code: 'App Icon Name',
                        detail: 'Validation Error',
                        title: 'The app icon name must contain the app store name. Please re-name the app icon and clear this error to try again.'
                    }
                }
            ]
        } else {
            const alertIndex = event.detail.context.alerts && event.detail.context.alerts.find(alert => alert.error.code === 'App Icon Name')
            alertIndex && event.detail.context.alerts.splice(alertIndex, 1)
        }

        dispatch('change', {
            formKey: 'schoolOnboarding',
            keypath: 'attributes.steps.INITIAL_DESIGN_REVIEW.launchIconName',
            index: undefined,
            value: [ event.detail.value ],
            context
        })
    }

    const addBlankUser = () => {
        const blankUser = {
            $create: true,
            id: context.form.users.length,
            attributes: {
                firstname: '',
                lastname: '',
                email: '',
                phoneNumber: '',
                sports: ['*'],
                roles: [ 'admin' ],
                schoolIds: [schoolId],
                $admin: true,
                $notificationManager: false,
                $rewardsManager: false,
                $attributesToPreserve: [ 'roles' ]
            }
        }

        if (context.school && context.school.organizationId) {
            blankUser.organizationIds = [context.school.organizationId]
        }

        context.form.users = [...context.form.users, blankUser]
    }

</script>

<style>
    .fno-app-icon {
        max-width: 100%;
    }

    .notification-example {
        font-size: 12px;
        background-color: white;
        border-radius: 10px;
        padding: 5px;
        box-shadow: 1px 1px 1px #888888;
    }
</style>

<h1>General Information</h1>
<p>Let's ensure we have the correct contact information on file.</p>

<Row class="mb-3">
    <Col xs="12" md="8" lg="6" xl="5">
        <InputText
            label="School/Organization"
            placeholder="School of Hard Knocks"
            {context}
            formKey="school"
            keypath="attributes.name"
            orgsValue={orgsData["legal_name"]}
            bind:value={context.form.school.attributes.name}
            on:change
            disabled={tier !== 'tier3'}
        />
    </Col>
    <Col xs="12" md="8" lg="6" xl="5">
        <InputText
            label="School/Organization Short Name"
            placeholder="SHK"
            helptext="This name will be used to represent your teams in automatic game score notifications. Frequently set as an acronym or a shortened version of your school name."
            {context}
            formKey="school"
            keypath="attributes.nicknameShort"
            bind:value={context.form.school.attributes.nicknameShort}
            on:change
            disabled={tier !== 'tier3'}
        />
        <blockquote style="padding: 1% 40% 0 5%;">
            <p class="notification-example">
                <b>🏀 (W) Halftime</b>
                <br>
                {context.form.school.attributes.nicknameShort || 'SHK'} - 34
                <br>
                Opponent - 17
            </p>
        </blockquote>
    </Col>
    <Col xs="12" md="8" lg="6" xl="5">
        <Select
            label="Timezone"
            {context}
            required
            includeDefaultValue
            options={selectableTimezones}
            formKey="school"
            keypath="attributes.timezone"
            bind:value={context.form.school.attributes.timezone}
            on:change
            disabled={tier !== 'tier3'}
        />
    </Col>
    <Col xs="12" md="8" lg="6" xl="5">
        <InputText
            label="Zip Code"
            placeholder="i.e. 00000"
            helptext="The zip code of your school"
            {context}
            required
            formKey="school"
            keypath="attributes.zipCode"
            orgsValue={orgsData["zip_code"]}
            bind:value={context.form.school.attributes.zipCode}
            on:change
            disabled={tier !== 'tier3'}
        />
    </Col>
</Row>

<Row>
    <Col xs="12" md="8" lg="6" xl="5">
        <InputFile
            label={tier === 'tier3' ? 'Primary App Logo' : 'Default Team Logo Image'}
            helptext={tier === 'tier3' ? "The primary logo will appear in locations such as the affiliations menu and filter menu. Recommended file type and size: 500 x 500 pixels, .png" : "The default logo will appear in locations such as the filter menu. Recommended file type and size: 500 x 500 pixels, .png"}
            {context}
            formKey="clientOnboarding"
            keypath="attributes.steps.GENERAL_INFO.primaryAppLogo"
            bind:value={context.form.clientOnboarding.attributes.steps.GENERAL_INFO.primaryAppLogo}
            fileUploader={postPublicFile}
            on:change
        />
    </Col>
    <Col xs="12" md="8" lg="6" xl="5">
        {#if primaryLogo}
            <img class="fno-app-icon" src={primaryLogo} alt="preview of primary logo" />
        {/if}
    </Col>
</Row>

{#if tier === 'tier3'}
    <h2 class="mt-5">
        App Naming
    </h2>

    <p>
        There are two app-naming placements regarding the app. The App Store Name is the long name
        found in the app in the stores. The App Icon Name is the short name that will display underneath
        the launch icon on the home screen of the user’s device.
    </p>

    <Row>
        <Col xs="12" md="8" lg="6" xl="5">
            <InputText
                label="App Store Name"
                {context}
                maxLength={30}
                formKey="schoolOnboarding"
                keypath="attributes.steps.INITIAL_DESIGN_REVIEW.appStoreName"
                bind:value={context.form.schoolOnboarding.attributes.steps.INITIAL_DESIGN_REVIEW.appStoreName}
                on:change />
        </Col>
        <Col xs="12" md="8" lg="6" xl="5">
            <InputText
                label="App Icon Name"
                {context}
                maxLength={12}
                formKey="schoolOnboarding"
                keypath="attributes.steps.INITIAL_DESIGN_REVIEW.launchIconName"
                bind:value={context.form.schoolOnboarding.attributes.steps.INITIAL_DESIGN_REVIEW.launchIconName}
                on:change={validateIconName} />
        </Col>
    </Row>

    <Row class="mb-5">
        <Col xs="12" md="8" lg="10" xl="10">
            <Alert color="info">
                <Icon data={infoCircle} />
                The App Store Name and App Icon Name must be related due to Apple's Policies. For
                example, an App Store Name of Snapton Athletics and a Launch Icon Name of Go Bulls
                would be prohibited because "Go Bulls" is not included in the App Store Name.
            </Alert>
        </Col>
    </Row>
{/if}

<h2>Portal Users</h2>

{#each context.form.users as user, index (user.id)}
    <Row class="mb-3">
        <Col>
            <UserInfo
                bind:context
                formKey="users"
                {index}
                on:change
                {rewardsEnabled}
                disabled={tier !== 'tier3' && !user.$create}
            />
        </Col>
    </Row>
{/each}

<Row>
    <Col>
        <p>
            <Button on:click={addBlankUser}>
                Add another contact
                <Icon data={plus} />
            </Button>
        </p>
    </Col>
</Row>

<FormAlerts bind:alerts={context.alerts} />

<FormSavingButtons 
    firstPage 
    {context} 
    {saving} 
    on:save 
    on:nextStepClicked={() => dispatch('nextStepClicked', { context, tier })}
    on:previousStepClicked 
/>
